import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {IoniconsFilled} from "@pt/pt-models";
import {NgbDateStruct, NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {TranslateService} from "@ngx-translate/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {NotificationCreateRequest, NotificationPatchRequest} from "@primatoo-ms-organization";
import {ReminderPayload} from "./models";
import { LogService, NotificationsService} from "@pt/api-client";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";

@Component({
  selector: 'pt-details-reminder',
  template: `
    <ng-container *ngIf="reminderPayload?.notification === null else Reminder">
      <div
        container='body'
        placement='bottom'
        ngbTooltip="{{btnText | translate}}"
        (click)="reminderPayload.enabled ? openDialog() : null" *ngIf="reminderPayload.visible"
        [ngStyle]="{'opacity-50': !reminderPayload.enabled}">
        <pt-icon class='dph-button' [size]='18' [key]='IconReminder'></pt-icon>
      </div>
    </ng-container>

    <ng-template #Reminder>
      <div class="d-flex align-items-center" style="margin-top: 1px" (click)="openDialog()">
        <pt-icon class="me-1" iconType="IONIC_ICON" [key]="IconReminder" [size]="16"
                 style="margin-bottom: 1px"></pt-icon>
        <span class="me-1">{{ "LABELS.REMINDER" | translate }}</span>
        <span>{{ reminderPayload?.notification?.date | localizedDate }}</span>
      </div>
    </ng-template>

    <ng-template #reminderDialog>
      <div (click)="$event.stopPropagation()">

        <div class="modal-header" style="flex-direction: column; align-items: flex-start">
          <div class="h4 modal-title">{{ "TITLE.REMINDER" | translate }}</div>
          <p class="m-0">{{ "MESSAGE.REMINDER" | translate }}</p>
        </div>

        <div class="modal-body">

          <div class="confirm-delete-placeholder" *ngIf="showConfirm">
            <label class='m-0 me-2'>{{ "MESSAGE.REMINDER_DELETE" | translate }}</label>
            <div class="d-flex mt-2">
              <pt-button-with-loader
                [label]='"BUTTON.CONFIRM" | translate'
                [loading]='loading'
                (buttonWithLoaderClick)='deleteReminder()'
              ></pt-button-with-loader>
              <button class='btn btn-sm btn-outline-secondary ms-1'
                      (click)='showConfirm = false'>
                {{ "BUTTON.CANCEL" | translate }}
              </button>
            </div>
          </div>

          <form *ngIf="formGroup" [formGroup]="formGroup">
            <div class='w-50 mb-2'>
              <pt-input-timestamp
                [label]="'LABELS.DATE' | translate"
                [showTimePicker]="false"
                [isRequired]="true"
                [minDate]="minDate"
                formControlName='date'>
              </pt-input-timestamp>
            </div>

            <pt-input-text-area
              [minRow]='15'
              [marginBottom]="'0'"
              [inputId]='"note"'
              formControlName='note'
              [label]="'LABELS.NOTE' | translate"
            ></pt-input-text-area>
          </form>
        </div>

        <div class="modal-footer">
          <ng-container *ngIf="!edit else CheckFooter">
            <div class="w-100 d-flex justify-content-end me-0 ms-0">
              <button class="btn btn-sm btn-primary me-1"
                      (click)="createReminder()" [disabled]="formGroup?.invalid || showConfirm">
                {{ "BUTTON.ADD" | translate }}
              </button>
              <button class="btn btn-sm btn-outline-secondary"
                      [disabled]="showConfirm"
                      (click)="closeDialog()">
                {{ "BUTTON.CANCEL" | translate }}
              </button>
            </div>
          </ng-container>

          <ng-template #CheckFooter>
            <div class="w-100 d-flex align-items-center justify-content-between me-0 ms-0">
              <button class='btn btn-sm btn-danger d-flex align-items-end'
                      [disabled]="showConfirm"
                      (click)='showConfirm = true'>
                {{ "BUTTON.DELETE" | translate }}
              </button>

              <div class="d-flex ms-1">
                <button class="btn btn-sm btn-primary me-1"
                        (click)="updateReminder()"
                        [disabled]="showConfirm || formGroup?.invalid || formGroup.pristine">
                  {{ "BUTTON.SAVE" | translate }}
                </button>
                <button class="btn btn-sm btn-outline-secondary "
                        [disabled]="showConfirm"
                        (click)="closeDialog()">
                  {{ "BUTTON.CANCEL" | translate }}
                </button>
              </div>
            </div>
          </ng-template>
        </div>
      </div>
    </ng-template>
  `,
  styles: [
    `
      .confirm-delete-placeholder {
        position: absolute;
        top: -10px;
        left: 0;
        bottom: 0;
        width: 100%;
        height: calc(100% + 50px);
        /*// calc(100% + 48px);*/
        border-radius: 20px;
        background: #fff;
        z-index: 1000;
        padding: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
      }
    `
  ]
})
export class DetailsReminderComponent implements OnInit, OnDestroy {

  @ViewChild('reminderDialog', {static: true}) reminderDialog: TemplateRef<unknown>;

  @Input() btnType: 'btn' | 'flat' = 'flat';
  @Input() showText = true;
  @Input() btnText = '';

  @Input() reminderPayload: ReminderPayload;
  @Output() reloadAction = new EventEmitter<boolean>();

  // usato per test

  public IconReminder = IoniconsFilled.calendarCheck;
  public formGroup: FormGroup;
  public language: string;
  public edit = false;
  public loading = false;
  public minDate?: NgbDateStruct;
  public showConfirm = false;

  private modalRef: NgbModalRef;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private modalService: NgbModal,
              private translateService: TranslateService,
              private logService: LogService,
              private notificationsService: NotificationsService
  ) {
    this.formGroup = new FormGroup({
      date: new FormControl(null, [Validators.required]),
      note: new FormControl()
    })
  }

  ngOnInit(): void {
    this.language = this.translateService.currentLang;
    this.setForm();
    const oggi = new Date();
    const domani = new Date(oggi);
    domani.setDate(oggi.getDate() + 1);

    this.minDate = {
      day: domani.getDate(),
      month: domani.getMonth() + 1,
      year: domani.getFullYear()
    }
  }

  setForm() {
    if (this.reminderPayload.notification !== null && this.reminderPayload.notification !== undefined) {
      const date = this.convertToLocal(this.reminderPayload.notification?.date);
      if (this.logService.isLoggable('DEBUG')) {
        console.log('[DEBUG] TIMESTAMP 1 (UTC):   ', this.reminderPayload.notification?.date);
        console.log('[DEBUG] TIMESTAMP 2 (LOCAL): ', date);
      }

      this.formGroup.setValue({
        date,
        note: this.reminderPayload.notification?.note
      });
      this.formGroup.markAsPristine();
      this.edit = true;
    } else {
      this.edit = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  openDialog() {
    this.setForm();
    this.showConfirm = false;
    this.modalRef = this.modalService.open(this.reminderDialog, {
      centered: true,
      backdrop: 'static',
      size: 'md'
    });
  }

  closeDialog() {
    this.modalRef?.close();
    this.formGroup?.reset();
  }

  createReminder() {
    const form = this.formGroup.getRawValue();
    const date = this.convertToUTC(form.date);
    if (this.logService.isLoggable('DEBUG')) {
      console.log('[DEBUG] TIMESTAMP 1 (LOCAL): ', form.date);
      console.log('[DEBUG] TIMESTAMP 2 (UTC):   ', date);
    }
    const request: NotificationCreateRequest = {
      date,
      note: form.note,
      docId: this.reminderPayload?.docId,
      docType: this.reminderPayload?.docType
    }

    this.notificationsService.createNotification(request).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.reloadAction.emit(true);
      this.closeDialog();
    });
  }
  convertToUTC(timestamp: number): number {
    const date = new Date(timestamp);

    // Ottieni il fuso orario locale in minuti e converti in millisecondi
    const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;

    // Sottrai l'offset per ottenere il timestamp UTC
    return timestamp - timezoneOffsetMs;
  }
  convertToLocal(timestampUTC: number): number {
    const date = new Date(timestampUTC);

    // Ottieni l'offset del browser in millisecondi
    const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;

    // Aggiungi l'offset per ottenere l'orario locale
    return timestampUTC + timezoneOffsetMs;
  }

  updateReminder() {
    const form = this.formGroup.getRawValue();

    const date = this.convertToUTC(form.date);
    if (this.logService.isLoggable('DEBUG')) {
      console.log('[DEBUG] TIMESTAMP 1 (LOCAL): ', form.date);
      console.log('[DEBUG] TIMESTAMP 2 (UTC):   ', date);
    }
    const request: NotificationPatchRequest = {
      note: form.note,
      date
    }
    this.notificationsService.updateNotification(this.reminderPayload?.notification?.id, request).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.reloadAction.emit(true);
      this.closeDialog();
    });
  }

  deleteReminder() {
    this.loading = true
    this.notificationsService.deleteNotification(this.reminderPayload.notification.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loading = false;
      this.reloadAction.emit(true);
      this.closeDialog();
    }, () => {
      this.loading = false;
      this.reloadAction.emit(true);
    });
  }
}
